import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from "react";
import { EditSectionModal } from "../EditSectionModal/EditSectionModal";
import { Typography } from "@/frontend/shared/ui/Typography";
import { Input } from "@/frontend/shared/ui/Input";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { UploadLogo } from "@/frontend/shared/ui/UploadLogo/UploadLogo";
import { EditIcon, PlusIcon, TrashIcon, UserIcon } from "@/frontend/shared/assets/icons";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import styles from "./EditTeamModal.module.scss";
import { EllipsisIcon } from "@/frontend/shared/assets/icons/EllipsisIcon";
import { ConfirmationModal } from "@/frontend/shared/ui/ConfirmationModal/ConfirmationModal";
import { postUpdateEmployeesLatest } from "../../model/services/postUpdateEmployeesLatest";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { AxiosError } from "axios";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { validateName } from "@/frontend/shared/lib/validations/validateName";
import { validateText } from "@/frontend/shared/lib/validations/validateText";
import { validateLinkedInUrl } from "@/frontend/shared/lib/validations/validateLinkedInUrl";
import { validateLogo } from "@/frontend/shared/lib/validations/validateLogo";

type TEditTeamModal = {
  isOpen: boolean;
  onClose: () => void;
  teamData: TEmployee[];
  triggerRefetch: () => void;
};

enum ESections {
  KEY_PEOPLE = "key-people",
  ADD_EMPLOYEE = "add-employee"
}

export type TEmployee = {
  imageUrl: string | null;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  linkedInUrl: string | null;
};

function isBase64Image(image: string) {
  return image?.startsWith("data:image/") ?? false;
}

export function EditTeamModal({ isOpen, onClose, teamData, triggerRefetch }: TEditTeamModal) {
  const [shownSection, setShownSection] = useState<ESections>(ESections.KEY_PEOPLE);
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const formattedTeamData = useMemo(
    () =>
      teamData.map((item) => {
        return {
          imageUrl: item.imageUrl || "",
          firstName: item.firstName || "",
          lastName: item.lastName || "",
          title: item.title || "",
          linkedInUrl: item.linkedInUrl || ""
        };
      }),
    [teamData]
  );
  const [keyPeopleData, setKeyPeopleData] = useState(formattedTeamData);
  const [editIndex, setEditIndex] = useState(-1);
  const [addEmployeeData, setAddEmployeeData] = useState({
    imageUrl: "",
    firstName: "",
    lastName: "",
    title: "",
    linkedInUrl: ""
  });

  const [inputErrors, setInputErrors] = useState<{
    firstName?: string;
    lastName?: string;
    title?: string;
    linkedInUrl?: string;
    imageUrl?: string;
  }>({});

  useEffect(() => {
    setKeyPeopleData(formattedTeamData);
  }, [formattedTeamData]);

  const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] = useState({ isOpen: false, index: -1 });

  function onEditKeyPeople(e: MouseEvent<SVGSVGElement>, index: number) {
    e.stopPropagation();
    setShownSection(ESections.ADD_EMPLOYEE);
    setAddEmployeeData(keyPeopleData[index]);
    setIsEditing(true);
    setEditIndex(index);
  }

  function onDeleteKeyPeople(index: number) {
    setKeyPeopleData((prev) => prev.filter((_, i) => i !== index));
    setIsOpenDeleteConfirmation({ isOpen: false, index: -1 });
  }

  function onChangeFirstName(value: string) {
    setAddEmployeeData((prev) => {
      return { ...prev, firstName: value };
    });
  }

  function onChangeLastName(value: string) {
    setAddEmployeeData((prev) => {
      return { ...prev, lastName: value };
    });
  }

  function onChangeTitle(value: string) {
    setAddEmployeeData((prev) => {
      return { ...prev, title: value };
    });
  }

  function onChangeLinkedInUrl(value: string) {
    setAddEmployeeData((prev) => ({ ...prev, linkedInUrl: value }));
  }

  function validate() {
    setInputErrors({});

    const firstNameError = validateName({
      name: addEmployeeData.firstName,
      maxLength: 35,
      fieldName: "First Name"
    });
    const lastNameError = validateName({
      name: addEmployeeData.lastName,
      maxLength: 35,
      fieldName: "Last Name",
      required: false
    });
    const titleError = validateText({
      text: addEmployeeData.title,
      fieldName: "Title",
      maxLength: 100,
      required: false
    });
    const linkedInError = validateLinkedInUrl({ linkedInUrl: addEmployeeData.linkedInUrl, required: false });
    const imageUrlError = validateLogo({ url: addEmployeeData.imageUrl, required: false, fieldName: "Image URL" });

    setInputErrors({
      firstName: firstNameError,
      lastName: lastNameError,
      title: titleError,
      linkedInUrl: linkedInError,
      imageUrl: imageUrlError
    });

    return !firstNameError && !lastNameError && !titleError && !linkedInError && !imageUrlError;
  }

  async function onSave() {
    if (shownSection === ESections.ADD_EMPLOYEE) {
      if (!validate()) {
        snackbarConstructor("Please correct the errors before saving.", "error");
        return;
      }
    }

    if (shownSection === ESections.ADD_EMPLOYEE && (addEmployeeData.firstName || addEmployeeData.lastName)) {
      if (isEditing && editIndex !== -1) {
        setKeyPeopleData((prev) => prev.map((item, i) => (i === editIndex ? { ...item, ...addEmployeeData } : item)));
        setIsEditing(false);
        setEditIndex(-1);
      } else {
        const newEmployee = { ...addEmployeeData };
        setKeyPeopleData((prev) => [...prev, newEmployee]);
      }
      setAddEmployeeData({ imageUrl: "", firstName: "", lastName: "", title: "", linkedInUrl: "" });
      setShownSection(ESections.KEY_PEOPLE);
    } else {
      onClose();
      setShownSection(ESections.KEY_PEOPLE);

      const data = keyPeopleData.map((item, index) => ({
        ...item,
        firstName: item.firstName || null,
        lastName: item.lastName || "",
        title: item.title || null,
        linkedInUrl: item.linkedInUrl || "",
        rank: index + 1,
        imageUrl: !isBase64Image(item.imageUrl) ? item.imageUrl : undefined,
        base64Image: isBase64Image(item.imageUrl) ? item.imageUrl : undefined
      }));

      try {
        await postUpdateEmployeesLatest({ employees: data });
        snackbarConstructor("Team data updated successfully", "normal");
      } catch (e) {
        reportErrorToSentry(e as AxiosError, "EDIT_TEAM_MODAL: /founder-revisions/employees");
        snackbarConstructor("Error updating team data", "error");
      }
      triggerRefetch();
    }
  }

  function onDragEnd(result: DropResult) {
    const { source, destination } = result;
    if (!destination) {
      return;
    }
    const items = Array.from(keyPeopleData);
    const [reorderedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, reorderedItem);

    setKeyPeopleData(items);
  }

  function content(section: ESections) {
    if (section === ESections.KEY_PEOPLE) {
      return (
        <div className={styles.keyPeopleContainer}>
          <Typography variant="label-4" color="greyscale700" className={styles.label}>
            Key People
          </Typography>
          <Droppable droppableId="keyPeople">
            {(provided) => {
              return (
                keyPeopleData?.length > 0 && (
                  <div className={styles.employeesContainer} ref={provided.innerRef} {...provided.droppableProps}>
                    {keyPeopleData.map((employee, index) => (
                      <Draggable key={index} draggableId={`item-${index}`} index={index}>
                        {(provided) => (
                          <div
                            key={index}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={styles.employee}
                            {...provided.dragHandleProps}
                          >
                            <div className={styles.employeeContent}>
                              <div className={styles.dragIcon}>
                                <EllipsisIcon className={styles.ellipsisIcon} />
                              </div>
                              {employee.imageUrl ? (
                                <img src={employee.imageUrl} alt="employee" className={styles.image} />
                              ) : (
                                <div className={styles.image}>
                                  <UserIcon className={styles.userIcon} />
                                </div>
                              )}
                              <a
                                className={styles.employeeInfo}
                                href={employee.linkedInUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Typography variant="label-4" color="greyscale900">
                                  {employee.firstName} {employee.lastName}
                                </Typography>
                                <Typography variant="body-3" color="greyscale600">
                                  {employee.title}
                                </Typography>
                              </a>
                            </div>
                            <div className={styles.iconsContainer}>
                              <EditIcon
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditKeyPeople(e, index);
                                }}
                              />
                              <TrashIcon
                                onClick={(e) => setIsOpenDeleteConfirmation({ isOpen, index })}
                                className={styles.trashIcon}
                              />
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )
              );
            }}
          </Droppable>
          <NormalButton
            className={styles.btnAddEmployee}
            size="large"
            variant="secondary"
            onClick={() => setShownSection(ESections.ADD_EMPLOYEE)}
          >
            <PlusIcon />
            Add People
          </NormalButton>
        </div>
      );
    } else {
      return (
        <div className={styles.addEmployeeContainer}>
          <UploadLogo
            onUpload={(e) => setAddEmployeeData((prev) => ({ ...prev, imageUrl: e }))}
            image={addEmployeeData.imageUrl}
            onRemove={() => setAddEmployeeData((prev) => ({ ...prev, imageUrl: "" }))}
            text="Upload Image"
            imageStyle={styles.logo}
          />
          <div className={styles.nameContainer}>
            <div className={styles.nameInput}>
              <Typography variant="label-4" color="greyscale700" className={styles.label}>
                First Name
              </Typography>
              <Input
                className={styles.input}
                value={addEmployeeData.firstName}
                onChange={(e) => onChangeFirstName(e.target.value)}
                error={inputErrors.firstName}
              />
            </div>
            <div className={styles.nameInput}>
              <Typography variant="label-4" color="greyscale700" className={styles.label}>
                Last Name
              </Typography>
              <Input
                className={styles.input}
                value={addEmployeeData.lastName}
                onChange={(e) => onChangeLastName(e.target.value)}
                error={inputErrors.lastName}
              />
            </div>
          </div>
          <div className={styles.titleContainer}>
            <Typography variant="label-4" color="greyscale700" className={styles.label}>
              Title
            </Typography>
            <Input
              className={styles.input}
              value={addEmployeeData.title}
              onChange={(e) => onChangeTitle(e.target.value)}
              error={inputErrors.title}
            />
          </div>
          <div className={styles.linkedinContainer}>
            <Typography variant="label-4" color="greyscale700" className={styles.label}>
              LinkedIn
            </Typography>
            <Input
              className={styles.input}
              value={addEmployeeData.linkedInUrl}
              onChange={(e) => onChangeLinkedInUrl(e.target.value)}
              error={inputErrors.linkedInUrl}
            />
          </div>
        </div>
      );
    }
  }

  const title = shownSection === ESections.KEY_PEOPLE ? "Edit Team" : "Edit Employee";
  function onConfirmCancel() {
    if (shownSection === ESections.ADD_EMPLOYEE) {
      setIsOpenConfirmationModal(false);
      setAddEmployeeData({ imageUrl: "", firstName: "", lastName: "", title: "", linkedInUrl: "" });
      setShownSection(ESections.KEY_PEOPLE);
    } else {
      setIsOpenConfirmationModal(false);
      setShownSection(ESections.KEY_PEOPLE);
      setKeyPeopleData(formattedTeamData);
      onClose();
    }
  }

  function hasChanges() {
    if (shownSection === ESections.ADD_EMPLOYEE) {
      const initialData = {
        imageUrl: "",
        firstName: "",
        lastName: "",
        title: "",
        linkedInUrl: ""
      };
      return JSON.stringify(addEmployeeData) !== JSON.stringify(initialData);
    } else {
      return JSON.stringify(keyPeopleData) !== JSON.stringify(formattedTeamData);
    }
  }

  function onCloseModal() {
    if (hasChanges()) {
      setIsOpenConfirmationModal(true);
    } else if (shownSection === ESections.ADD_EMPLOYEE) {
      setShownSection(ESections.KEY_PEOPLE);
    } else {
      onClose();
    }
  }

  return (
    <EditSectionModal isOpen={isOpen} onClose={onCloseModal} title={title} width={"600px"} onSave={onSave}>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className={styles.container}>{content(shownSection)}</div>
        <ConfirmationModal
          isOpen={isOpenConfirmationModal}
          onClose={() => setIsOpenConfirmationModal(false)}
          onConfirm={onConfirmCancel}
          content={"Are you sure you want to discard the changes you made?"}
          title={"Discard Changes"}
          confirmButtonText={"Discard"}
          cancelButtonText={"No Thanks"}
        />
        <ConfirmationModal
          isOpen={isOpenDeleteConfirmation.isOpen}
          onClose={() => setIsOpenDeleteConfirmation({ isOpen: false, index: -1 })}
          onConfirm={() => onDeleteKeyPeople(isOpenDeleteConfirmation.index)}
          content={"Are you sure you want to delete team member?"}
          title={"Delete Team Member"}
          confirmButtonText={"Delete"}
          cancelButtonText={"Cancel"}
          dangerConfirm
        />
      </DragDropContext>
    </EditSectionModal>
  );
}
